import type { ProcedureInitiative, Stored } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	ProcedureInitiativeEqualsFilterModifier,
	ProcedureInitiativeExcludesFilterModifier,
	ProcedureInitiativeIncludesFilterModifier,
	ProcedureInitiativeNotEqualsFilterModifier,
} from "../../modifiers/procedureInitiative"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { ProcedureInitiativeFilterCell } from "./ProcedureInitiativeFilterCell"

type ProcedureInitiativeFilterDefValue = ProcedureInitiative["uuid"] | null
type ProcedureInitiativeFilterDefFilterValue =
	| (ProcedureInitiative["uuid"] | null)[]
	| ProcedureInitiative["uuid"]
	| null

export class ProcedureInitiativeFilterDef extends BaseFilterDef<
	ProcedureInitiativeFilterDefValue,
	ProcedureInitiativeFilterDefFilterValue
> {
	readonly id = "procedure_initiative"
	readonly defaultFilterValue: ProcedureInitiativeFilterDefFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly procedureInitiatives: Stored<ProcedureInitiative>[]

	constructor(procedureInitiatives: Stored<ProcedureInitiative>[]) {
		super()
		this.procedureInitiatives = procedureInitiatives
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: ProcedureInitiativeEqualsFilterModifier,
			isValueValid: (_value: ProcedureInitiativeFilterDefValue): _value is ProcedureInitiative["uuid"] | null =>
				true,
			isFilterValueValid: (
				filterValue: ProcedureInitiativeFilterDefFilterValue,
			): filterValue is ProcedureInitiative["uuid"] | null => !Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: ProcedureInitiativeNotEqualsFilterModifier,
			isValueValid: (_value: ProcedureInitiativeFilterDefValue): _value is ProcedureInitiative["uuid"] | null =>
				true,
			isFilterValueValid: (
				filterValue: ProcedureInitiativeFilterDefFilterValue,
			): filterValue is ProcedureInitiative["uuid"] | null => !Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: ProcedureInitiativeIncludesFilterModifier,
			isValueValid: (_value: ProcedureInitiativeFilterDefValue): _value is ProcedureInitiative["uuid"] | null =>
				true,
			isFilterValueValid: (
				filterValue: ProcedureInitiativeFilterDefFilterValue,
			): filterValue is (ProcedureInitiative["uuid"] | null)[] => Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: ProcedureInitiativeExcludesFilterModifier,
			isValueValid: (_value: ProcedureInitiativeFilterDefValue): _value is ProcedureInitiative["uuid"] | null =>
				true,
			isFilterValueValid: (
				filterValue: ProcedureInitiativeFilterDefFilterValue,
			): filterValue is (ProcedureInitiative["uuid"] | null)[] => Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<ProcedureInitiativeFilterDef>): ReactNode {
		return <ProcedureInitiativeFilterCell key={props.filter.id} {...props} />
	}
}
